import { useEffect, useRef, useState } from "react";
import { FiEdit, FiTrash } from "react-icons/fi";
import { getApiInstance } from "../../../utils/api";
import InputSearch from "../../../components/inputs/inputSearch/inputSearch";
import Button from "../../../components/button/button";
import Modal from "../../../components/modal/modal";
import Input from "../../../components/inputs/input/input";
import InputCheckbox from "../../../components/inputs/inputCheckbox/inputCheckbox";
import Notification from "../../../components/notification/notification";
import Popup from "../../../components/popup/popup";
import Table from "../../../components/table/table";
import SectionHeader from "../../../components/sectionHeader/sectionHeader";
import Badge from "../../../components/badge/badge";
import { searchByKeywords } from "../../../utils/searchUtils";

export default function GestionMoneda() {
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [searchTerm, setSearchTerm] = useState("")
	const [monedas, setMonedas] = useState([])
	const [selectedMoneda, setSelectedMoneda] = useState(null)
	const [monedaAEliminar, setMonedaAEliminar] = useState(null)
	const [isPopupOpen, setIsPopupOpen] = useState(false)
	const notificationRef = useRef(null)
	const [filteredMonedas, setFilteredMonedas] = useState([]);

	const [nombreError, setNombreError] = useState("")
	const [valorError, setValorError] = useState("")
	const [simboloError, setSimboloError] = useState("")

	const [newMoneda, setNewMoneda] = useState({
		nombreMoneda: "",
		principal: false,
		valor: "",
		simbolo: ""
	})

	useEffect(() => {
		fetchMonedas()
	}, [])

		useEffect(() => {
		setFilteredMonedas(monedas);
	}, [monedas]);

	const fetchMonedas = async () => {
		const api = getApiInstance()
		try {
			const response = await api.get("/api/Moneda/GetAllMonedas")
			setMonedas(response.data)
		} catch (error) {
			console.error("Error al obtener las monedas:", error)
			setMonedas([])
		}
	}

	const openModal = () => {
		setSelectedMoneda(null)
		setNewMoneda({ nombreMoneda: "", principal: false, valor: "" })
		setIsModalOpen(true)
	}

	const closeModal = () => {
		setIsModalOpen(false)
		setSelectedMoneda(null)
		setNewMoneda({ nombreMoneda: "", principal: false, valor: "" })
	}

    const handleEditMoneda = (moneda) => {
        setSelectedMoneda(moneda);
        setNewMoneda({
            nombreMoneda: moneda.nombreMoneda,
            principal: moneda.principal,
            valor: moneda.valor !== undefined ? moneda.valor.toString() : "",
			simbolo: moneda.simbolo || ""
        });
        setIsModalOpen(true);
    };

	const confirmDeleteMoneda = (moneda) => {
		console.log("Moneda a eliminar:", moneda)
		setMonedaAEliminar(moneda)
		setIsPopupOpen(true)
	}

	const handleConfirmDelete = async () => {
		if (!monedaAEliminar || !monedaAEliminar.codMoneda) {
			console.error("Error: No hay moneda seleccionada para eliminar.")
			return
		}

		const api = getApiInstance()
		try {
			const response = await api.delete(
				`/api/Moneda/${monedaAEliminar.codMoneda}`
			)

			if (response.status === 200) {
				notificationRef.current?.addNotification(
					`La moneda ${monedaAEliminar.nombreMoneda} ha sido eliminada con éxito.`,
					"success"
				)

				setMonedas((prevMonedas) =>
					prevMonedas.filter((m) => m.codMoneda !== monedaAEliminar.codMoneda)
				)
			}

			setIsPopupOpen(false)
		} catch (error) {
			const backendMessage = error.response?.data?.message || "Error al eliminar la moneda.";
		
			notificationRef.current?.addNotification(
				backendMessage,
				"error"
			);
		
			setIsPopupOpen(false);
		
			console.error("Error al eliminar la moneda:", backendMessage);
		}
		
	}

	const handleCancelDelete = () => {
		setIsPopupOpen(false)
	}

	const handleInputChange = (e) => {
		const { name, value } = e.target
		setNewMoneda((prev) => ({ ...prev, [name]: value }))
	}

	const handleCheckboxChange = () => {
		setNewMoneda((prev) => ({ ...prev, principal: !prev.principal }))
	}

	const handleAddMoneda = async () => {
		setNombreError("")
		setValorError("")
		setSimboloError("")

		if (!newMoneda.nombreMoneda) {
			setNombreError("El campo nombre es obligatorio.")
			return
		}

		if (!newMoneda.simbolo) {
			setSimboloError("El campo símbolo es obligatorio.")
			return
		}

		const api = getApiInstance()
		try {
			if (selectedMoneda) {
				await api.post(
					`/api/Moneda/ChangeMonedaValue/${selectedMoneda.codMoneda}`,
					{
						...newMoneda,
						valor: parseFloat(newMoneda.valor),
						fecha: new Date().toISOString()
					}
				)
				notificationRef.current?.addNotification(
					`Moneda ${newMoneda.nombreMoneda} actualizada con éxito.`,
					"success"
				)
			} else {
				const response = await api.post("/api/Moneda", {
					...newMoneda,
					valor: parseFloat(newMoneda.valor)
				})
				setMonedas([...monedas, response.data])
				notificationRef.current?.addNotification(
					"Moneda añadida con éxito.",
					"success"
				)
			}
			fetchMonedas()
			closeModal()
		} catch (error) {
			console.error(
				"Error al añadir o editar moneda:",
				error.response?.data || error
			)
		}
	}

    const columnsMonedas = [
        { key: "", label: "" },
        { key: "nombreMoneda", label: "Nombre" },
        { key: "valor", label: "Tipo cambio" },
		{ key: "simbolo", label: "Símbolo" },
        {
            key: "principal",
            label: "Principal",
            render: (row) => (
                <Badge
                    label={row.principal ? "Principal" : "Secundaria"}
                    variant={row.principal ? "success" : "default"}
                />
            ),
        },
    ];

	const actions = (row) => [
		{
			label: "Editar",
			onClick: () => handleEditMoneda(row),
			icon: <FiEdit />
		},
		{
			label: "Eliminar",
			onClick: () => confirmDeleteMoneda(row),
			icon: <FiTrash />
		}
	]

	const handleSearch = (query) => {
		if (!query.trim()) {
			setFilteredMonedas(monedas);
			return;
		}
		const resultados = searchByKeywords(monedas, query, [
			"nombreMoneda",
			"valor",
			"simbolo"
		]);
		setFilteredMonedas(resultados);
	};

	return (
		<section className="dashboard-usuarios">
			<SectionHeader
				title="Panel de Administrador - Gestión de moneda"
				subtitle="Aquí puedes gestionar las monedas de la aplicación"
			/>

			<div className="dashboard-usuarios__content__table-header">
				<InputSearch
					context="tableSearch"
					placeholder="Buscar..."
					onSearch={handleSearch}
				/>
				<Button label="Añadir moneda" variant="filled" onClick={openModal} />
			</div>

			<Table
				columns={columnsMonedas}
				data={filteredMonedas}
				actions={actions}
				noResultsMessage="No se encontraron monedas."
			/>

			<Modal
				title={selectedMoneda ? "Editar Moneda" : "Añadir Moneda"}
				isOpen={isModalOpen}
				onClose={closeModal}
				onAccept={handleAddMoneda}
			>
				<div className="dashboard-usuarios__modal">
					<form
						className="dashboard-usuarios__modal__content"
						autoComplete="off"
					>
						<Input
							type="text"
							label="Nombre"
							name="nombreMoneda"
							placeholder="Introduce el nombre de la moneda"
							value={newMoneda.nombreMoneda}
							onChange={handleInputChange}
							disabled={selectedMoneda !== null}
						/>
						{nombreError && <p className="error">{nombreError}</p>}

						<Input
							type="number"
							label="Valor"
							name="valor"
							placeholder="Introduce el valor de la moneda"
							value={newMoneda.valor}
							onChange={handleInputChange}
						/>
						{valorError && <p className="error">{valorError}</p>}

						<Input
							type="text"
							label="Símbolo monetario"
							name="simbolo"
							placeholder="Ej: €, $, MXN"
							value={newMoneda.simbolo}
							onChange={handleInputChange}
						/>

						<InputCheckbox
							label="Es moneda principal"
							checked={newMoneda.principal}
							onChange={handleCheckboxChange}
						/>
					</form>
				</div>
			</Modal>

			{isPopupOpen && (
				<Popup
					type="alert"
					alertType="warning"
					titleMessage="Confirmar eliminación"
					alertMessage="¿Estás seguro de que deseas eliminar este elemento?"
					onConfirm={handleConfirmDelete}
					onClose={handleCancelDelete}
					confirmLabel="Eliminar"
					cancelLabel="Cancelar"
				/>
			)}

			<Notification ref={notificationRef} />
		</section>
	)
}
